import { useCallback, useState } from 'react';
import {
  addMonths,
  eachMonthOfInterval,
  format,
  isSameMonth,
  startOfMonth,
  startOfYear,
} from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';

export type AvgDealSizeByMonthRow = {
  monthKey: string;
  label: string;
  avgValue: number;
  count: number;
  isCurrentMonth: boolean;
};

function monthKeyInTimezone(iso: string, timezone: string) {
  return format(toZonedTime(new Date(iso), timezone), 'yyyy-MM');
}

/**
 * Average won-deal value per month, January through the current month
 * (business timezone). Won deals are grouped by `updated_at` (close date),
 * matching the "Avg deal size (won)" KPI.
 */
export function useAvgDealSizeByMonthYtdBreakdown() {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timezone =
    selectedBusiness?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    'UTC';

  const [months, setMonths] = useState<AvgDealSizeByMonthRow[]>([]);
  const [yearAvg, setYearAvg] = useState(0);
  const [yearCount, setYearCount] = useState(0);
  const [yearLabel, setYearLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!supabase || !businessId) {
      setMonths([]);
      setYearAvg(0);
      setYearCount(0);
      setYearLabel('');
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const zonedNow = toZonedTime(new Date(), timezone);
      const yearStartZoned = startOfYear(zonedNow);
      const currentMonthZoned = startOfMonth(zonedNow);

      setYearLabel(format(zonedNow, 'yyyy'));

      const rangeStart = fromZonedTime(yearStartZoned, timezone).toISOString();
      const rangeEnd = fromZonedTime(
        startOfMonth(addMonths(zonedNow, 1)),
        timezone
      ).toISOString();

      const { data, error: queryError } = await supabase
        .from('pipeline_opportunities')
        .select('value, updated_at')
        .eq('business_id', businessId)
        .eq('status', 'Won')
        .gte('updated_at', rangeStart)
        .lt('updated_at', rangeEnd);

      if (queryError) throw queryError;

      const sumByMonth = new Map<string, number>();
      const countByMonth = new Map<string, number>();
      let yearSum = 0;
      let yearWonCount = 0;

      for (const deal of data ?? []) {
        if (!deal.updated_at) continue;
        const key = monthKeyInTimezone(deal.updated_at, timezone);
        const value = Number(deal.value) || 0;
        sumByMonth.set(key, (sumByMonth.get(key) ?? 0) + value);
        countByMonth.set(key, (countByMonth.get(key) ?? 0) + 1);
        yearSum += value;
        yearWonCount += 1;
      }

      const monthDates = eachMonthOfInterval({
        start: yearStartZoned,
        end: currentMonthZoned,
      });

      const rows: AvgDealSizeByMonthRow[] = monthDates.map((month) => {
        const key = format(month, 'yyyy-MM');
        const count = countByMonth.get(key) ?? 0;
        const sum = sumByMonth.get(key) ?? 0;
        return {
          monthKey: key,
          label: format(month, 'MMMM yyyy'),
          avgValue: count > 0 ? sum / count : 0,
          count,
          isCurrentMonth: isSameMonth(month, zonedNow),
        };
      });

      setMonths(rows);
      setYearCount(yearWonCount);
      setYearAvg(yearWonCount > 0 ? yearSum / yearWonCount : 0);
    } catch (err) {
      console.error('Error loading avg deal size by month YTD breakdown:', err);
      setMonths([]);
      setYearAvg(0);
      setYearCount(0);
      setError('Could not load deal size breakdown.');
    } finally {
      setLoading(false);
    }
  }, [supabase, businessId, timezone]);

  return { months, yearAvg, yearCount, yearLabel, loading, error, load };
}
